import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import clsx from "clsx";

export interface FilterOption {
  value: string;
  label: string;
  meta?: string;
}

interface Props {
  label: string;
  value: string | null;
  options: FilterOption[];
  onChange: (value: string | null) => void;
  searchable?: boolean;
  allLabel?: string;
}

/**
 * A compact, clean filter dropdown. Shows `Label: <selection>` with a chevron;
 * opens a popover with an optional search box and an "All" reset row. Active
 * (a non-null value) gets a filled treatment. Presentational — the caller
 * supplies options and owns the value.
 */
export default function FilterSelect({
  label,
  value,
  options,
  onChange,
  searchable = false,
  allLabel = "All",
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const selected = value === null ? null : (options.find((o) => o.value === value) ?? null);
  const term = q.trim().toLowerCase();
  const visible = term ? options.filter((o) => o.label.toLowerCase().includes(term)) : options;

  const pick = (v: string | null) => {
    onChange(v);
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm transition",
          selected
            ? "bg-fg/[0.10] text-mut-100 border-fg/[0.18]"
            : "bg-transparent text-mut-300 border-fg/[0.08] hover:bg-fg/[0.04] hover:text-mut-100",
        )}
      >
        <span className="text-mut-300">{label}:</span>
        <span className={selected ? "text-mut-100" : "text-mut-300"}>{selected?.label ?? allLabel}</span>
        <ChevronDown className="w-3.5 h-3.5 text-mut-500 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 min-w-[200px] panel p-1.5 max-h-72 overflow-y-auto">
          {searchable && (
            <input
              autoFocus
              className="input mb-1.5"
              placeholder={`Search ${label.toLowerCase()}…`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          )}
          <button
            type="button"
            className="cmdk-item w-full"
            data-selected={value === null ? "true" : "false"}
            onClick={() => pick(null)}
          >
            <span className="flex-1">{allLabel}</span>
            {value === null && <Check className="w-3.5 h-3.5 text-accent-azure" />}
          </button>
          {visible.map((o) => (
            <button
              key={o.value}
              type="button"
              className="cmdk-item w-full"
              data-selected={value === o.value ? "true" : "false"}
              onClick={() => pick(o.value)}
            >
              <span className="truncate flex-1">{o.label}</span>
              {o.meta && <span className="meta">{o.meta}</span>}
              {value === o.value && <Check className="w-3.5 h-3.5 text-accent-azure" />}
            </button>
          ))}
          {visible.length === 0 && <div className="px-3 py-2 text-sm text-mut-300">No matches.</div>}
        </div>
      )}
    </div>
  );
}
